import { InfoOutlineIcon, QuestionOutlineIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  ButtonGroup,
  Center,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  ListItem,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Radio,
  RadioGroup as ChakraRadioGroup,
  Stack,
  Text,
  Tooltip,
  UnorderedList,
  useBoolean,
  useClipboard,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { Input } from '@gamma/ui/form-fields';
import CheckSharpIcon from '@gamma/ui/icons/CheckSharpIcon';
import ContentCopySharpIcon from '@gamma/ui/icons/ContentCopySharpIcon';
import RefreshSharpIcon from '@gamma/ui/icons/RefreshSharpIcon';
import VisibilityOffSharpIcon from '@gamma/ui/icons/VisibilityOffSharpIcon';
import VisibilitySharpIcon from '@gamma/ui/icons/VisibilitySharpIcon';
import { Alert, Modal } from '@gamma/ui/overlay';
import { DatePicker } from '@gamma/ui/form-fields';
import { Label } from '@gamma/ui/form-fields';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useMemo } from 'react';
import moment from 'moment';
import { useApolloClient } from '@apollo/client';
import { i18n } from '../../../../shared/localization';
import { useRotateAPIKey } from '../../queries';
import {
  calculateExpirationDate,
  ExpirationOption,
} from '@investigator/shared/utils';

const { rotateAPIKeyModal, rotateButtonTooltip, expirationValidation, forms } =
  i18n.pages.apiKeys;

interface RotateAPIKeyProps {
  id: string;
  name: string;
  end_ts?: number | null;
}

interface RotateAPIKeyFormValues {
  expirationOption: ExpirationOption;
  customExpirationDate?: Date;
}

export const RotateAPIKey = ({ id, name, end_ts }: RotateAPIKeyProps) => {
  const client = useApolloClient();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const isExpired = useMemo(() => {
    if (!end_ts) return false;
    return moment.unix(end_ts).isBefore(moment());
  }, [end_ts]);

  const methods = useForm<RotateAPIKeyFormValues>({
    defaultValues: {
      expirationOption: isExpired
        ? ExpirationOption.Days180
        : ExpirationOption.NoChange,
      customExpirationDate: undefined,
    },
    mode: 'onChange',
  });
  const { handleSubmit, watch, reset: formReset } = methods;
  const showToastMessage = useToast();
  const [rotateAPIKeyDefinition, { data, loading, reset }] = useRotateAPIKey({
    onError: () => {
      showToastMessage({
        status: 'error',
        description: rotateAPIKeyModal.apiError,
        isClosable: true,
        position: 'bottom-right',
      });
    },
  });
  const [isAPIKeyHidden, setIsAPIKeyHidden] = useBoolean(true);
  const [isSuccessAlertShowing, setIsSuccessAlertShowing] = useBoolean(true);
  const createdAPIKey = data?.rotateAPIKeyDefinition?.api_key;
  const isAPIKeyRotated = !!createdAPIKey;
  const selectedExpirationOption = watch('expirationOption');

  const { onCopy, hasCopied } = useClipboard(createdAPIKey || '', {
    timeout: 2000,
  });

  const tomorrowStartOfDay = useMemo(() => {
    const date = new Date();
    date.setDate(date.getDate() + 1);
    date.setHours(0, 0, 0, 0);
    return date;
  }, []);

  const resetModalState = () => {
    formReset({
      expirationOption: isExpired
        ? ExpirationOption.Days180
        : ExpirationOption.NoChange,
      customExpirationDate: undefined,
    });
    reset();
  };

  const handleOpenModal = () => {
    resetModalState();
    onOpen();
  };

  const handleCloseModal = () => {
    if (isAPIKeyRotated) {
      client.cache.evict({ fieldName: 'listAPIKeyDefinitions' });
      client.cache.gc();
    }
    resetModalState();
    onClose();
  };

  const handleRotateAPIKey = handleSubmit(async (formData) => {
    const { expirationOption, customExpirationDate } = formData;

    if (expirationOption === ExpirationOption.NoChange) {
      await rotateAPIKeyDefinition({
        variables: {
          id,
          end_ts,
        },
      });
      formReset();
      return;
    }

    const expirationDate = calculateExpirationDate(
      expirationOption,
      customExpirationDate,
    );

    await rotateAPIKeyDefinition({
      variables: {
        id,
        end_ts: expirationDate ? moment(expirationDate).unix() : null,
      },
    });
    formReset();
  });

  return (
    <>
      <Tooltip label={rotateButtonTooltip}>
        <IconButton
          size="box-md"
          variant={'solid'}
          aria-label="rotate-api-key"
          colorScheme="gray"
          onClick={(e) => {
            e.stopPropagation();
            handleOpenModal();
          }}
          icon={<RefreshSharpIcon />}
        />
      </Tooltip>
      <Modal
        hideDivider
        isOpen={isOpen}
        onOpen={onOpen}
        onClose={handleCloseModal}
        closeOnEsc={!loading && !isAPIKeyRotated}
        closeOnOverlayClick={!loading && !isAPIKeyRotated}
        isCloseDisabled={loading}
        isCentered
        size="lg"
        title={
          isAPIKeyRotated
            ? rotateAPIKeyModal.rotated.title
            : i18n.formatString(rotateAPIKeyModal.title, name)
        }
        body={
          isAPIKeyRotated ? (
            <VStack alignItems="left" gap={4}>
              <Alert
                onClose={setIsSuccessAlertShowing.off}
                isClosable
                isOpen={isSuccessAlertShowing}
                status="success"
                variant={'left-accent'}
                title={rotateAPIKeyModal.rotated.alertTitle}
              >
                {rotateAPIKeyModal.rotated.alertBody}
              </Alert>
              <VStack w="full" gap={0.5}>
                <HStack align={'center'} gap={2} w="full">
                  <FormControl>
                    <FormLabel
                      display={'inline-flex'}
                      justifyContent={'center'}
                      alignItems={'center'}
                      gap={2}
                    >
                      {rotateAPIKeyModal.rotated.inputTitle}
                      <Popover trigger="hover" placement="right-end">
                        <PopoverTrigger>
                          <QuestionOutlineIcon />
                        </PopoverTrigger>
                        <PopoverContent w={'50%'} h={'auto'}>
                          <PopoverBody borderRadius="5px">
                            <Text
                              align={'left'}
                              fontWeight={'normal'}
                              fontSize={'smaller'}
                            >
                              {rotateAPIKeyModal.rotated.infoText}
                            </Text>
                          </PopoverBody>
                        </PopoverContent>
                      </Popover>
                    </FormLabel>
                    <Input
                      label=""
                      value={createdAPIKey}
                      type={isAPIKeyHidden ? 'password' : 'text'}
                      isReadOnly
                      name="api-key"
                      rightElement={
                        <IconButton
                          onClick={setIsAPIKeyHidden.toggle}
                          aria-label="toggle-api-key"
                          icon={
                            isAPIKeyHidden ? (
                              <VisibilitySharpIcon w="18px" h="18px" />
                            ) : (
                              <VisibilityOffSharpIcon w="18px" h="18px" />
                            )
                          }
                        />
                      }
                    />
                  </FormControl>
                  <IconButton
                    onClick={onCopy}
                    aria-label="copy-api-key"
                    alignSelf={'flex-end'}
                    icon={<ContentCopySharpIcon w="18px" h="18px" />}
                  />
                </HStack>
                {hasCopied && (
                  <Text
                    alignSelf={'start'}
                    fontSize="sm"
                    display={'inline-flex'}
                    justifyContent={'center'}
                    alignItems={'center'}
                    gap={0.5}
                  >
                    <Center
                      width={'min-content'}
                      height={'min-content'}
                      borderRadius={'100%'}
                      p={0.25}
                      bg={'green'}
                    >
                      <CheckSharpIcon
                        color={'white'}
                        w="15px"
                        h="15px"
                        transform={'auto'}
                        scaleX={0.8}
                        scaleY={0.8}
                      />
                    </Center>
                    &nbsp;&nbsp;
                    {rotateAPIKeyModal.rotated.inputCopiedMessage}
                  </Text>
                )}
              </VStack>
              <VStack align={'left'}>
                <Text aria-required mb={-1}>
                  {rotateAPIKeyModal.rotated.permissions}
                </Text>
                <Box>
                  <Text fontWeight={'semibold'}>
                    {rotateAPIKeyModal.rotated.detections}
                  </Text>
                  <UnorderedList fontWeight={'light'}>
                    <ListItem>{rotateAPIKeyModal.rotated.readAccess}</ListItem>
                  </UnorderedList>
                </Box>
                <Box>
                  <Text fontWeight={'semibold'}>
                    {rotateAPIKeyModal.rotated.alerts}
                  </Text>
                  <UnorderedList fontWeight={'light'}>
                    <ListItem>{rotateAPIKeyModal.rotated.readAccess}</ListItem>
                  </UnorderedList>
                </Box>
                <Box>
                  <Text fontWeight={'semibold'}>
                    {rotateAPIKeyModal.rotated.logs}
                  </Text>
                  <UnorderedList fontWeight={'light'}>
                    <ListItem>{rotateAPIKeyModal.rotated.readAccess}</ListItem>
                  </UnorderedList>
                </Box>
              </VStack>
            </VStack>
          ) : (
            <FormProvider {...methods}>
              <VStack alignItems="left" gap={4}>
                <Text
                  fontWeight={'semibold'}
                  display={'inline-flex'}
                  justifyContent={'start'}
                  alignItems={'center'}
                  gap={2}
                  mb={-0.5}
                >
                  <InfoOutlineIcon w={'17.5px'} h={'17.5px'} />
                  {rotateAPIKeyModal.rotateConsequencesTitle}
                </Text>
                <UnorderedList mt={-2} ml={5}>
                  {rotateAPIKeyModal.rotateConsequencesItems.map(
                    (item, idx) => (
                      <ListItem key={idx}>{item}</ListItem>
                    ),
                  )}
                </UnorderedList>
                <Box
                  w="100%"
                  maxW="100%"
                  p={1}
                  data-testid="expiration-section"
                >
                  <Controller
                    name="expirationOption"
                    control={methods.control}
                    rules={{
                      required: expirationValidation.expirationOptionRequired,
                    }}
                    render={({ field }) => (
                      <Label
                        name="expirationOption"
                        label={forms.expiration.newKeyExpirationLabel}
                        isRequired
                        data-testid="expiration-option-radio"
                      >
                        <ChakraRadioGroup
                          name="expirationOption"
                          id="expirationOption"
                          value={field.value}
                          onChange={field.onChange}
                        >
                          <Stack direction="column" gap={0.5}>
                            {!isExpired && (
                              <Radio
                                value={ExpirationOption.NoChange}
                                isDisabled={loading}
                              >
                                {forms.expiration.options.noChange}
                              </Radio>
                            )}
                            <Radio
                              value={ExpirationOption.Days180}
                              isDisabled={loading}
                            >
                              {forms.expiration.options['180days']}
                            </Radio>
                            <Radio
                              value={ExpirationOption.OneYear}
                              isDisabled={loading}
                            >
                              {forms.expiration.options['1year']}
                            </Radio>
                            <Radio
                              value={ExpirationOption.Custom}
                              isDisabled={loading}
                            >
                              {forms.expiration.options.custom}
                            </Radio>
                            {selectedExpirationOption ===
                              ExpirationOption.Custom && (
                              <Box
                                w="100%"
                                maxW="100%"
                                pl={6}
                                mt={1}
                                mb={1}
                                data-testid="custom-date-picker-container"
                                sx={{
                                  '& > *': {
                                    width: '100%',
                                  },
                                  '& button': {
                                    width: '100%',
                                    minWidth: '100%',
                                  },
                                }}
                              >
                                <Controller
                                  name="customExpirationDate"
                                  control={methods.control}
                                  rules={{
                                    required:
                                      selectedExpirationOption === 'custom'
                                        ? expirationValidation.customDateRequired
                                        : false,
                                    validate: (value) => {
                                      if (
                                        selectedExpirationOption === 'custom' &&
                                        value
                                      ) {
                                        const selectedDate =
                                          moment(value).startOf('day');
                                        const minDate =
                                          moment(tomorrowStartOfDay).startOf(
                                            'day',
                                          );
                                        if (selectedDate.isBefore(minDate)) {
                                          return expirationValidation.dateMustBeTomorrow;
                                        }
                                      }
                                      return true;
                                    },
                                  }}
                                  render={({
                                    field: dateField,
                                    fieldState,
                                  }) => (
                                    <DatePicker
                                      name="customExpirationDate"
                                      isDisabled={loading}
                                      label=""
                                      isLabelHidden={true}
                                      selectedDate={dateField.value}
                                      onChange={dateField.onChange}
                                      minDate={tomorrowStartOfDay}
                                      error={fieldState.error?.message}
                                      data-testid="custom-expiration-date-picker"
                                      width="100%"
                                    />
                                  )}
                                />
                              </Box>
                            )}
                            <Radio
                              value={ExpirationOption.NoExpiration}
                              isDisabled={loading}
                            >
                              {forms.expiration.options.noExpiration}
                            </Radio>
                          </Stack>
                        </ChakraRadioGroup>
                      </Label>
                    )}
                  />
                </Box>
              </VStack>
            </FormProvider>
          )
        }
        footer={
          isAPIKeyRotated ? (
            <Button
              variant="solid"
              colorScheme="gray"
              onClick={handleCloseModal}
              isDisabled={loading}
            >
              {rotateAPIKeyModal.rotated.buttons.close}
            </Button>
          ) : (
            <HStack>
              <ButtonGroup>
                <Button
                  variant="solid"
                  colorScheme="gray"
                  onClick={handleCloseModal}
                  isDisabled={loading}
                >
                  {rotateAPIKeyModal.buttons.cancel}
                </Button>
                <Button
                  variant="solid"
                  colorScheme="blue"
                  onClick={handleRotateAPIKey}
                  isDisabled={loading}
                  isLoading={loading}
                >
                  {rotateAPIKeyModal.buttons.generateNewKey}
                </Button>
              </ButtonGroup>
            </HStack>
          )
        }
      />
    </>
  );
};
